import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { SwUpdate, UnrecoverableStateEvent } from '@angular/service-worker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

/**
 * @description
 * Reloads the app when the service worker reports it can no longer serve itself, typically because cached
 * resources went missing.
 *
 * Reloading reinstalls a clean version, which only works with connectivity. Offline we keep the running app
 * alive and wait for the connection to come back, because reloading offline would land on the browser error
 * page and lock the inspector out of an in-progress inspection.
 * @see https://angular.dev/ecosystem/service-workers/communications#handling-an-unrecoverable-state
 */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class SwUnrecoverableStateService {
  public readonly _swUpdate = inject(SwUpdate);
  public readonly _qpLoggerService = inject(QpLoggerService);

  private readonly _document = inject(DOCUMENT);

  public init(): void {
    this._swUpdate.unrecoverable.pipe(untilDestroyed(this)).subscribe({
      next: (event: UnrecoverableStateEvent): void => {
        this._qpLoggerService.error('💥 Service worker is in an unrecoverable state', event.reason);
        this._reloadWhenOnline();
      },
      error: (error: Error): void => {
        this._qpLoggerService.error(error);
      },
    });
  }

  private _reloadWhenOnline(): void {
    if (navigator.onLine) {
      this._document.location.reload();

      return;
    }

    this._qpLoggerService.warn('💥 Offline, the app shell will be repaired as soon as the connection is back');
    this._document.defaultView?.addEventListener('online', (): void => this._document.location.reload(), { once: true });
  }
}
